import { Fragment } from "react";
import { Divider, List, Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import CardContainer from "../CardContainer";
import { CardAction } from "./CardAction";
import { CardContent, buildChartProps } from "./CardContent";
import { DataCardFilterChips } from "./DataCardFilterChips";
import { DataLegend } from "./DataLegend";
import { InsightDetailsContent } from "./InsightDetailsContent";
import { SPACING } from "./constants";
import type {
	ChartType,
	DataCardCustomization,
	DataCardProps,
	ExtendedDetailItem,
} from "./types";

type DataCardMainProps<T extends ChartType> = {
	data: DataCardProps<T>["data"];
	config: DataCardProps<T>["config"];
	handlers: DataCardProps<T>["handlers"];
	customization: DataCardProps<T>["customization"];
	theme: DataCardProps<T>["theme"];
	cardStyles: Record<string, unknown>;
	showExtDetails: boolean;
	onInsightPress: (() => void) | undefined;
	insightDetails?: DataCardCustomization["insightDetails"];
	showInsightDetails?: boolean;
};

const ExtendedDetails = ({
	items,
	showDividers,
}: { items: ExtendedDetailItem[]; showDividers: boolean }) => {
	const theme = useAppTheme();

	if (items.length === 0) {
		return null;
	}

	return (
		<Box paddingTop={SPACING.lg}>
			<Divider />
			<Box paddingTop={SPACING.lg}>
				{items.map((item, index) => (
					<Fragment key={`${item.label}-${index}`}>
						<List.Item
							title={item.subtitle}
							description={item.label}
							style={{
								paddingLeft: SPACING.card,
								paddingRight: SPACING.card,
							}}
							left={
								item.icon
									? () => (
											<List.Icon
												icon={() => item.icon}
												style={{ marginLeft: 0, marginRight: 0 }}
											/>
										)
									: undefined
							}
							right={() => (
								<Text
									variant="labelSmall"
									style={{
										color: theme.colors.onSurfaceVariant,
										alignSelf: "center",
									}}
								>
									{String(item.value)}
								</Text>
							)}
							titleStyle={{
								color: theme.colors.onSurfaceVariant,
								fontSize: 12,
								fontWeight: "500",
								lineHeight: 16,
								letterSpacing: 0.5,
							}}
							descriptionStyle={{ color: theme.colors.onSurface }}
						/>
						{showDividers && index < items.length - 1 && <Divider />}
					</Fragment>
				))}
			</Box>
		</Box>
	);
};

export const DataCardMain = <T extends ChartType = ChartType>({
	data,
	config,
	handlers,
	customization,
	theme: appTheme,
	cardStyles,
	showExtDetails,
	onInsightPress,
	insightDetails,
	showInsightDetails,
}: DataCardMainProps<T>) => {
	const chartContent = (
		<CardContent
			{...buildChartProps(config.chartType, data.chartData, data.threshold)}
			theme={appTheme}
			customChartComponent={customization?.chartComponent}
		/>
	);

	const cardActions = (
		<CardAction
			insightText={customization?.insightText}
			animateInsight={customization?.animateInsight}
			onInsightPress={onInsightPress}
			ctaLabel={customization?.ctaLabel}
			onCtaPress={handlers?.onCtaPress}
		/>
	);

	return (
		<Box>
			<CardContainer
				title={config.headerTitle}
				subtitle={config.headerSubtitle ?? ""}
				icon={config.headerIconName ?? "chart-line"}
				tooltip={config.tooltip}
				actions={cardActions}
			>
				<Box style={{ paddingVertical: SPACING.lg, paddingHorizontal: 0 }}>
					{config.showFilters && config.filterOptions && (
						<Box paddingHorizontal={SPACING.card} paddingBottom={SPACING.lg}>
							<DataCardFilterChips
								options={config.filterOptions}
								selectedValue={config.defaultFilter}
								onFilterChange={handlers?.onFilterChange}
							/>
						</Box>
					)}

					<Box gap={SPACING.lg}>
						{chartContent}

						{config.showLegend && data.legendData && (
							<DataLegend items={data.legendData} />
						)}
					</Box>

					{showExtDetails && data.extendedDetailsData && (
						<ExtendedDetails
							items={data.extendedDetailsData}
							showDividers={!config.expandable}
						/>
					)}
				</Box>
			</CardContainer>

			{showInsightDetails && insightDetails && (
				<InsightDetailsContent
					cardContent={chartContent}
					detailedInsight={insightDetails.detailedInsight}
					recommendedActions={insightDetails.recommendedActions}
					followUpChips={insightDetails.followUpChips}
				/>
			)}
		</Box>
	);
};
